import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../api/axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, refreshToken: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    setUser(null);
    localStorage.removeItem('istmech_token');
    localStorage.removeItem('istmech_refresh_token');
    localStorage.removeItem('istmech_user'); // Clean up old key if exists
  };

  const fetchUser = useCallback(async (token: string) => {
    try {
      const response = await api.get('/users/me');
      const profileData = response.data;
      console.log('User Profile Data:', profileData); // Debugging

      // Handle potentially different casing from backend
      const firstName = profileData.firstName || profileData.first_name || '';
      const lastName = profileData.lastName || profileData.last_name || '';

      const user: User = {
        id: profileData.id,
        email: profileData.email,
        name: `${firstName} ${lastName}`.trim(),
        role: profileData.role
      };

      setUser(user);
      localStorage.setItem('istmech_user', JSON.stringify(user)); // Sync storage to avoid confusion
    } catch (error) {
      console.error('Error fetching user profile:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('istmech_token');
    if (token) {
      fetchUser(token);
    } else {
      setIsLoading(false);
    }
  }, [fetchUser]);

  const login = async (token: string, refreshToken: string) => {
    localStorage.setItem('istmech_token', token);
    localStorage.setItem('istmech_refresh_token', refreshToken);
    await fetchUser(token);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
